/**
 * GA4 (Firebase Analytics) 전송 레이어 — tracking.ts 의 유일한 백엔드.
 *
 * 앱(네이티브)은 @capacitor-firebase/analytics 플러그인이 google-services.json /
 * GoogleService-Info.plist 로 자동 초기화되고, 웹(선물 수신 외부 페이지)은
 * VITE_FIREBASE_* env 로 firebase JS SDK 를 lazy 초기화한다 (미설정 시 전체 no-op).
 *
 *                    ┌──────────────────────────────────────────┐
 *   logEvent() ───▶  │ state: idle → initializing → ready       │ ──▶ FirebaseAnalytics
 *   setUserId() ──▶  │              └───────────→ disabled(웹만) │      (plugin)
 *                    │ ready 전 호출은 pendingQueue 에 쌓였다가    │
 *                    │ ready 시 순서대로 flush (부팅 초기 유실 방지)│
 *                    └──────────────────────────────────────────┘
 *
 * GA4 제한(이벤트명 40자·영숫자_·파라미터 25개·문자열 100자)은 dev 에서 경고하고
 * 항상 안전하게 정규화해 보낸다. PII 방어(파라미터 allow-list)는 tracking.ts 담당.
 */
import { FirebaseAnalytics } from "@capacitor-firebase/analytics";
import { env, IS_DEV } from "../config/env";
import { isNative, getPlatform } from "../native";

type AnalyticsState = "idle" | "initializing" | "ready" | "disabled";

export type AnalyticsParams = Record<string, string | number | boolean>;

const GA4_EVENT_NAME_RE = /^[a-zA-Z][a-zA-Z0-9_]{0,39}$/;
const GA4_RESERVED_PREFIXES = ["firebase_", "google_", "ga_"] as const;
const GA4_MAX_PARAMS = 25;
const GA4_MAX_PARAM_KEY_LEN = 40;
const GA4_MAX_STRING_VALUE_LEN = 100;

let state: AnalyticsState = "idle";
/** ready 전에 들어온 호출 보관 큐 — ready 시 순서대로 flush. disabled 면 폐기. */
const pendingQueue: Array<() => void> = [];

function debugLog(...args: unknown[]): void {
  if (env.VITE_ENABLE_LOGGING || IS_DEV) {
    // eslint-disable-next-line no-console
    console.log("[analytics]", ...args);
  }
}

function devWarn(...args: unknown[]): void {
  if (IS_DEV) {
    // eslint-disable-next-line no-console
    console.warn("[analytics]", ...args);
  }
}

/**
 * 플러그인 호출 실패 원인 구분 — 침묵 실패로 두되 디버깅 가능하게 카테고리 로그.
 * (구버전 바이너리에 플러그인 없음 / Firebase 설정 오류 / 기타)
 */
function categorizePluginError(op: string, err: unknown): void {
  const msg = err instanceof Error ? err.message : String(err);
  let category = "unknown";
  if (/not implemented|not available|plugin/i.test(msg)) category = "plugin-missing";
  else if (/firebase|api.?key|app.?id|config/i.test(msg)) category = "config";
  else if (/network|fetch/i.test(msg)) category = "network";
  debugLog(`${op} failed (${category}):`, msg);
}

/** ready 면 즉시, 초기화 중이면 큐잉, disabled 면 폐기. */
function whenReady(run: () => void): void {
  if (state === "ready") {
    run();
    return;
  }
  if (state === "disabled") return;
  pendingQueue.push(run);
}

function flushQueue(): void {
  while (pendingQueue.length > 0) {
    const run = pendingQueue.shift();
    try {
      run?.();
    } catch (err: unknown) {
      categorizePluginError("flush", err);
    }
  }
}

const OTA_VERSION =
  typeof __APP_VERSION__ !== "undefined" ? __APP_VERSION__ : "0.0.0";

/** 초기 user property — 플랫폼 + OTA(웹빌드) 버전. 네이티브 바이너리 버전은 SDK 가 자동 수집. */
function setInitialUserProperties(): void {
  void FirebaseAnalytics.setUserProperty({
    key: "platform",
    value: getPlatform(),
  }).catch((err: unknown) => categorizePluginError("setUserProperty(platform)", err));
  void FirebaseAnalytics.setUserProperty({
    key: "ota_version",
    value: OTA_VERSION,
  }).catch((err: unknown) => categorizePluginError("setUserProperty(ota_version)", err));
}

/**
 * 앱 시작 시 1회 호출 (main.tsx). 이전에 들어온 호출은 큐에서 flush 된다.
 * - native: 플러그인이 네이티브 설정 파일로 이미 초기화됨 → 즉시 ready.
 * - web: VITE_FIREBASE_* 4종이 모두 있어야 firebase JS 초기화. 없으면 disabled.
 */
export function initAnalytics(): void {
  if (state !== "idle") return;

  if (isNative()) {
    state = "ready";
    setInitialUserProperties();
    flushQueue();
    return;
  }

  const config = {
    apiKey: env.VITE_FIREBASE_API_KEY,
    projectId: env.VITE_FIREBASE_PROJECT_ID,
    appId: env.VITE_FIREBASE_APP_ID,
    measurementId: env.VITE_FIREBASE_MEASUREMENT_ID,
  };
  if (!config.apiKey || !config.projectId || !config.appId || !config.measurementId) {
    state = "disabled";
    pendingQueue.length = 0;
    debugLog("disabled — VITE_FIREBASE_* web config 미설정");
    return;
  }

  state = "initializing";
  // firebase JS 는 웹에서만 필요 — dynamic import 로 네이티브 번들에서 분리.
  void import("firebase/app")
    .then(({ initializeApp }) => {
      initializeApp(config);
      state = "ready";
      setInitialUserProperties();
      flushQueue();
    })
    .catch((err: unknown) => {
      state = "disabled";
      pendingQueue.length = 0;
      categorizePluginError("initializeApp", err);
    });
}

/** GA4 제한에 맞게 파라미터 정규화 — 초과분은 잘라내고 dev 에서 경고. */
function normalizeParams(name: string, params: AnalyticsParams): AnalyticsParams {
  const entries = Object.entries(params);
  if (entries.length > GA4_MAX_PARAMS) {
    devWarn(`event "${name}": 파라미터 ${entries.length}개 — GA4 한도 ${GA4_MAX_PARAMS}개 초과분 제거`);
  }
  const normalized: AnalyticsParams = {};
  for (const [key, value] of entries.slice(0, GA4_MAX_PARAMS)) {
    if (key.length > GA4_MAX_PARAM_KEY_LEN) {
      devWarn(`event "${name}": 파라미터 키 "${key}" 40자 초과 — 제거`);
      continue;
    }
    if (typeof value === "string" && value.length > GA4_MAX_STRING_VALUE_LEN) {
      devWarn(`event "${name}": "${key}" 문자열 100자 초과 — 잘라서 전송`);
      normalized[key] = value.slice(0, GA4_MAX_STRING_VALUE_LEN);
      continue;
    }
    normalized[key] = value;
  }
  return normalized;
}

/** 이벤트 이름이 GA4 규칙에 맞는지 — 어긋나면 dev 경고 후 전송하지 않는다. */
function isValidEventName(name: string): boolean {
  if (!GA4_EVENT_NAME_RE.test(name)) {
    devWarn(`event "${name}": GA4 이름 규칙(영문 시작·영숫자_·40자) 위반 — 전송 생략`);
    return false;
  }
  if (GA4_RESERVED_PREFIXES.some((p) => name.startsWith(p))) {
    devWarn(`event "${name}": 예약 접두어 — 전송 생략`);
    return false;
  }
  return true;
}

/** 커스텀 이벤트 전송. ready 전 호출은 큐잉된다. */
export function logEvent(name: string, params: AnalyticsParams = {}): void {
  if (!isValidEventName(name)) return;
  const normalized = normalizeParams(name, params);
  whenReady(() => {
    void FirebaseAnalytics.logEvent({ name, params: normalized }).catch(
      (err: unknown) => categorizePluginError(`logEvent(${name})`, err),
    );
  });
}

/**
 * 화면 조회 — SPA 라우트 변경마다 useScreenTracking 이 호출.
 * screen_name 은 라우트 패턴(:param 정규화), page_path 는 원본 경로(디버깅용 파라미터).
 */
export function logScreenView(screenName: string, pagePath: string): void {
  const params = normalizeParams("screen_view", {
    screen_name: screenName,
    page_path: pagePath,
  });
  whenReady(() => {
    void FirebaseAnalytics.logEvent({ name: "screen_view", params }).catch(
      (err: unknown) => categorizePluginError("logEvent(screen_view)", err),
    );
  });
}

/** 로그인 시 내부 회원 식별자(가명 ID) 설정, 로그아웃 시 null. PII 금지. */
export function setAnalyticsUserId(userId: string | null): void {
  whenReady(() => {
    void FirebaseAnalytics.setUserId({ userId }).catch((err: unknown) =>
      categorizePluginError("setUserId", err),
    );
  });
}

/** user property 설정 — 호출 전 tracking.ts 의 allow-list 를 거칠 것. */
export function setAnalyticsUserProperty(key: string, value: string | null): void {
  whenReady(() => {
    void FirebaseAnalytics.setUserProperty({ key, value }).catch((err: unknown) =>
      categorizePluginError(`setUserProperty(${key})`, err),
    );
  });
}
